import { useEffect, useState } from 'react'
import { palettePrimaryMain } from '@/theme/designTokens'

import Dialog from '@mui/material/Dialog'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import OutlinedInput from '@mui/material/OutlinedInput'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { ApiError } from '@/api/http/types'
import {
  isNetworkApiError,
  messageFromTeamInviteSendError,
  TEAM_INVITE_SEND_NETWORK_ERROR_MESSAGE,
} from '@/shared/lib/apiErrorMessages'
import { useTeamMemberMutations } from '../api/useTeamMemberMutations'
import type { TeamInviteRole } from '../api/teamMembers.types'

const NAME_PATTERN = /^[a-zA-Z-]+$/

/** Linear-time invite email shape check; authoritative validation happens on the server. */
function isValidInviteEmailShape(value: string): boolean {
  const t = value.trim()
  if (!t || /\s/.test(t)) return false
  const at = t.indexOf('@')
  if (at <= 0) return false
  const domain = t.slice(at + 1)
  if (!domain || !domain.includes('.') || domain.startsWith('.') || domain.endsWith('.')) {
    return false
  }
  return !t.includes('@', at + 1)
}

const NAME_INVALID =
  'Name must be at least 2 characters and contain only letters or hyphens'
const DUPLICATE_EMAIL = 'This email address already exists in the system'

type InviteServerErrors = Partial<{
  firstName: string
  lastName: string
  email: string
  role: string
  /** Non-field errors (e.g. identity 400 without `details`). */
  form: string
}>

const INVITE_FIELD_KEYS = new Set(['firstName', 'lastName', 'email', 'role'])

/**
 * Maps platform invite failures to inline fields.
 * - Zod body validation: HTTP **422** + `error.code` `validation_error` + `details[]` (Postman may call this "400" in copy).
 * - Duplicate email (identity): HTTP **409** (often with `identity_service_error` code).
 */
function mapInviteApiErrorToServerErrors(err: ApiError): InviteServerErrors {
  const out: InviteServerErrors = {}

  if (isNetworkApiError(err)) {
    out.form = TEAM_INVITE_SEND_NETWORK_ERROR_MESSAGE
    return out
  }

  const duplicateByMessage = /already exists in the system/i.test(err.message)

  if (err.status === 409 || err.code === 'conflict' || duplicateByMessage) {
    out.email = DUPLICATE_EMAIL
    return out
  }

  if (err.details?.length) {
    for (const d of err.details) {
      if (INVITE_FIELD_KEYS.has(d.field)) {
        out[d.field as keyof Omit<InviteServerErrors, 'form'>] = d.issue
      }
    }
    if (Object.keys(out).length > 0) return out
  }

  const validationLike =
    err.isValidation || err.status === 422 || err.status === 400 || err.code === 'bad_request'

  if (validationLike) {
    const msg = err.message
    const lower = msg.toLowerCase()
    if (lower.includes('user role') || lower.includes('select a user role')) {
      out.role = msg
      return out
    }
    out.form = msg
    return out
  }

  out.form = err.message || TEAM_INVITE_SEND_NETWORK_ERROR_MESSAGE
  return out
}

function inviteFailureToastMessage(
  err: unknown,
  mapped: InviteServerErrors,
): string {
  return (
    mapped.form ??
    mapped.email ??
    mapped.firstName ??
    mapped.lastName ??
    mapped.role ??
    messageFromTeamInviteSendError(err)
  )
}

interface Props {
  open: boolean
  onClose: () => void
  onInvited?: () => void
  onInviteFailed?: (message: string) => void
}

function SelectDropdownIcon(props: object) {
  return <KeyboardArrowDownIcon {...props} sx={{ fontSize: 20, color: '#4a5565', mr: '12px' }} />
}

const labelSx = {
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#23262f',
  mb: '8px',
} as const

const baseInputSx = {
  height: '46px',
  width: '100%',
  bgcolor: '#ffffff',
  borderRadius: '10px',
  '& .MuiOutlinedInput-input': {
    px: '16px',
    py: '12px',
    fontSize: '14px',
    fontWeight: 400,
    color: '#23262f',
    letterSpacing: '-0.1504px',
    lineHeight: 'normal',
    '&::placeholder': { color: '#9ca3af', opacity: 1 },
  },
}

const inputSx = {
  ...baseInputSx,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e5e7eb',
    borderWidth: '1px',
  },
} as const

const inputErrorSx = {
  ...baseInputSx,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ef4444',
    borderWidth: '1px',
  },
} as const

const selectSx = {
  height: '46px',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: 400,
  color: '#23262f',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e5e7eb',
    borderWidth: '1px',
  },
} as const

const selectErrorSx = {
  ...selectSx,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ef4444',
    borderWidth: '1px',
  },
} as const

const errorTextSx = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#ef4444',
  mt: '4px',
} as const

const ROLE_OPTIONS: { value: TeamInviteRole; label: string }[] = [
  { value: 'EDITOR', label: 'Editor' },
  { value: 'VIEWER', label: 'Viewer' },
]

function validateInviteFirstName(value: string): string | null {
  const t = value.trim()
  if (!t) return 'First name is required'
  if (t.length < 2 || t.length > 100 || !NAME_PATTERN.test(t)) return NAME_INVALID
  return null
}

function validateInviteLastName(value: string): string | null {
  const t = value.trim()
  if (!t) return 'Last name is required'
  if (t.length < 2 || t.length > 100 || !NAME_PATTERN.test(t)) return NAME_INVALID
  return null
}

function validateInviteEmail(value: string): string | null {
  const t = value.trim()
  if (!t) return 'Email address is required'
  if (!isValidInviteEmailShape(t)) return 'Please enter a valid email address'
  return null
}

export function AddUserModal({
  open,
  onClose,
  onInvited,
  onInviteFailed,
}: Readonly<Props>) {
  const { invite } = useTeamMemberMutations()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<TeamInviteRole | ''>('')
  const [touched, setTouched] = useState(false)
  const [serverErrors, setServerErrors] = useState<InviteServerErrors>({})

  useEffect(() => {
    if (!open) return
    setFirstName('')
    setLastName('')
    setEmail('')
    setRole('')
    setTouched(false)
    setServerErrors({})
  }, [open])

  const firstErr = serverErrors.firstName ?? (touched ? validateInviteFirstName(firstName) : null)
  const lastErr = serverErrors.lastName ?? (touched ? validateInviteLastName(lastName) : null)
  const emailErr =
    serverErrors.email ?? (touched ? validateInviteEmail(email) : null)
  const roleErr =
    serverErrors.role ?? (touched && !role ? 'Please select a user role' : null)

  function handleClose() {
    if (invite.isPending) return
    onClose()
  }

  function handleSubmit() {
    setTouched(true)
    setServerErrors({})
    const fe = validateInviteFirstName(firstName)
    const le = validateInviteLastName(lastName)
    const ee = validateInviteEmail(email)
    if (fe || le || ee || !role) return

    invite.mutate(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        role,
      },
      {
        onSuccess: () => {
          onInvited?.()
          onClose()
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            const mapped = mapInviteApiErrorToServerErrors(err)
            setServerErrors(mapped)
            onInviteFailed?.(inviteFailureToastMessage(err, mapped))
            return
          }
          const msg = messageFromTeamInviteSendError(err)
          setServerErrors({ form: msg })
          onInviteFailed?.(msg)
        },
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      disableEscapeKeyDown={invite.isPending}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.5)' } },
      }}
      PaperProps={{
        sx: {
          bgcolor: '#ffffff',
          borderRadius: '10px',
          boxShadow:
            '0px 20px 25px 0px rgba(0,0,0,0.1), 0px 8px 10px 0px rgba(0,0,0,0.1)',
          width: 448,
          m: 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: '24px',
          pt: '24px',
          pb: '1px',
          borderBottom: '1px solid #e5e7eb',
          flexShrink: 0,
          minHeight: '101px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '28px',
              letterSpacing: '-0.4492px',
              color: '#23262f',
              whiteSpace: 'nowrap',
            }}
          >
            Add New User
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '-0.1504px',
              color: '#83888f',
              whiteSpace: 'nowrap',
            }}
          >
            Invite a new user to join your campaigns.
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={invite.isPending}
          aria-label="Close dialog"
          sx={{
            p: 0,
            mt: '4px',
            color: '#83888f',
            '&:hover': { bgcolor: 'transparent', color: '#23262f' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          px: '24px',
          pt: '24px',
          pb: '24px',
        }}
      >
        {serverErrors.form && (
          <Typography sx={{ ...errorTextSx, whiteSpace: 'normal' }}>{serverErrors.form}</Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography component="label" sx={labelSx}>
            First Name{' '}
            <Box component="span" sx={{ color: '#ef4444' }}>
              *
            </Box>
          </Typography>
          <OutlinedInput
            placeholder="Enter first name"
            fullWidth
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value)
              setServerErrors((s) => {
                const next = { ...s }
                delete next.firstName
                return next
              })
            }}
            disabled={invite.isPending}
            sx={firstErr ? inputErrorSx : inputSx}
          />
          {firstErr && <Typography sx={errorTextSx}>{firstErr}</Typography>}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography component="label" sx={labelSx}>
            Last Name{' '}
            <Box component="span" sx={{ color: '#ef4444' }}>
              *
            </Box>
          </Typography>
          <OutlinedInput
            placeholder="Enter last name"
            fullWidth
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value)
              setServerErrors((s) => {
                const next = { ...s }
                delete next.lastName
                return next
              })
            }}
            disabled={invite.isPending}
            sx={lastErr ? inputErrorSx : inputSx}
          />
          {lastErr && <Typography sx={errorTextSx}>{lastErr}</Typography>}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography component="label" sx={labelSx}>
            Email Address{' '}
            <Box component="span" sx={{ color: '#ef4444' }}>
              *
            </Box>
          </Typography>
          <OutlinedInput
            placeholder="Enter email address"
            fullWidth
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setServerErrors((s) => {
                const next = { ...s }
                delete next.email
                return next
              })
            }}
            disabled={invite.isPending}
            sx={emailErr ? inputErrorSx : inputSx}
          />
          {emailErr && <Typography sx={errorTextSx}>{emailErr}</Typography>}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography component="label" sx={labelSx} id="invite-role-label">
            User Role{' '}
            <Box component="span" sx={{ color: '#ef4444' }}>
              *
            </Box>
          </Typography>
          <FormControl fullWidth>
            <Select
              labelId="invite-role-label"
              displayEmpty
              value={role}
              onChange={(e) => {
                setRole(e.target.value as TeamInviteRole | '')
                setServerErrors((s) => {
                  const next = { ...s }
                  delete next.role
                  return next
                })
              }}
              disabled={invite.isPending}
              IconComponent={SelectDropdownIcon}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography sx={{ fontSize: '14px', color: '#9ca3af', fontWeight: 400 }}>
                      Select Role
                    </Typography>
                  )
                }
                const opt = ROLE_OPTIONS.find((o) => o.value === selected)
                return opt?.label ?? selected
              }}
              sx={roleErr ? selectErrorSx : selectSx}
            >
              {ROLE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {roleErr && <Typography sx={errorTextSx}>{roleErr}</Typography>}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
            height: '56px',
          }}
        >
          <Button
            variant="text"
            onClick={handleClose}
            disabled={invite.isPending}
            disableRipple
            sx={{
              height: '40px',
              px: '24px',
              borderRadius: '10px',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '-0.1504px',
              color: '#4a5565',
              textTransform: 'none',
              minWidth: 0,
              '&:hover': { bgcolor: 'transparent' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={invite.isPending}
            disableElevation
            sx={{
              height: '40px',
              px: '16px',
              borderRadius: '12px',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '16px',
              color: '#ffffff',
              bgcolor: palettePrimaryMain,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#0077b3' },
            }}
          >
            Send Invitation
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}
